import React from 'react';
import { Button, Stack } from '@mui/material';
import { WaiverOptionType } from '@prisma/client';
import { WaiverOption } from '../../../types/waivers';
import { SortableList } from '../../dnd/SortableList';
import OptionsListItem from '../OptionsListItem';
import CheckboxOption from '../CheckboxOption';
import TextSignatureOption from '../TextSignatureOption';
import useAwaitedForm from '../../../hooks/useAwaitedForm';

interface Props {
  options: WaiverOption[];
  onChange: (newOptions: WaiverOption[]) => void;
  disabled?: boolean;
  placeholder: string;
}

function WaiverSortableListItem({
  item,
  onChange,
  options,
  placeholder = 'Type an option',
  disabled,
}: Props & {
  item: WaiverOption;
}) {
  return (
    <OptionsListItem
      isLargeItem={item.type !== 'CHECKBOX'}
      options={options}
      item={item}
      disabled={disabled}
      onChange={onChange}>
      {item.type === 'CHECKBOX' ? (
        <CheckboxOption
          disabled={disabled}
          item={item}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
        />
      ) : (
        <TextSignatureOption
          disabled={disabled}
          item={item}
          options={options}
          onChange={onChange}
        />
      )}
    </OptionsListItem>
  );
}

function OptionsList({
  options,
  onChange,
  placeholder,
  disabled,
  addText = 'Add option',
  combineAllOptions = false,
  askForType = false,
}: Props & {
  addText?: string;
  combineAllOptions?: boolean;
  askForType?: boolean;
}) {
  const awaitedForm = useAwaitedForm();

  const checkboxOptions = React.useMemo(
    () => options.filter(option => option.type === 'CHECKBOX'),
    [options],
  );

  const nonCheckboxOptions = React.useMemo(
    () =>
      options.filter(
        option =>
          option.type === 'TEXT' ||
          option.type === 'MULTILINE_TEXT' ||
          option.type === 'SIGNATURE',
      ),
    [options],
  );

  const combinedOptions = React.useMemo(
    () => (combineAllOptions ? options : checkboxOptions),
    [combineAllOptions, options, checkboxOptions],
  );

  const onChangeOptions = React.useCallback(
    (newOptions: WaiverOption[]) => {
      // keep non-sorted options at the end
      const uncombinedOptions = combineAllOptions ? [] : nonCheckboxOptions;
      const newList = [...newOptions, ...uncombinedOptions].map(
        (option, index) => ({
          ...option,
          order: index + 1,
        }),
      );
      onChange(newList);
    },
    [combineAllOptions, nonCheckboxOptions, onChange],
  );

  const renderSortableItem = React.useCallback(
    (item: WaiverOption) => (
      <WaiverSortableListItem
        item={item}
        onChange={onChangeOptions}
        options={combinedOptions}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [onChangeOptions, combinedOptions, placeholder, disabled],
  );

  const renderTextSignatureItem = React.useCallback(
    (option: WaiverOption) => (
      <TextSignatureOption
        item={option}
        options={options}
        onChange={combineAllOptions ? onChangeOptions : onChange}
        disabled={disabled}
      />
    ),
    [options, combineAllOptions, onChangeOptions, onChange, disabled],
  );

  return (
    <Stack boxSizing="border-box">
      <SortableList
        items={combinedOptions}
        onChange={newOptions => {
          onChangeOptions(newOptions);
        }}
        renderItem={renderSortableItem}
      />
      <Button
        disabled={disabled}
        type="button"
        variant="smallLink"
        sx={{
          paddingLeft: 2,
          textAlign: 'left',
          width: 'fit-content',
        }}
        onClick={async () => {
          const type = askForType
            ? (
                await awaitedForm({
                  form: {
                    title: 'Add Question',
                    submitButtonText: 'Add',
                    initialValues: {
                      type: 'CHECKBOX' as WaiverOptionType,
                    },
                    inputRows: [
                      [
                        {
                          inputType: 'radio-select',
                          label: 'Input type',
                          name: 'type',
                          options: [
                            {
                              label: 'Checkbox',
                              value: 'CHECKBOX',
                            },
                            {
                              label: 'Text',
                              value: 'TEXT',
                            },
                            {
                              label: 'Multiline Text',
                              value: 'MULTILINE_TEXT',
                            },
                            {
                              label: 'Signature',
                              value: 'SIGNATURE',
                            },
                          ],
                        },
                      ],
                    ],
                  },
                  allowUntouched: true,
                })
              )?.type
            : 'CHECKBOX';

          if (!type) return;

          // only used locally
          const randomID = Math.random().toString(36).substring(7);
          onChangeOptions([
            ...combinedOptions,
            {
              id: randomID,
              text: '',
              order: combinedOptions.length + 1,
              type,
            },
          ]);
        }}>
        <span>{addText}</span>
      </Button>
      {!!nonCheckboxOptions?.length && !combineAllOptions && (
        <Stack mt={5} spacing={2} boxSizing="border-box">
          {nonCheckboxOptions.map(renderTextSignatureItem)}
        </Stack>
      )}
    </Stack>
  );
}

export default OptionsList;
